import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../config";
import { TrendingUp, Clock, Search, Globe } from "lucide-react";

const LANG_FLAGS = {
  en: "🇬🇧", sk: "🇸🇰", cs: "🇨🇿", de: "🇩🇪", pl: "🇵🇱", hu: "🇭🇺",
  fr: "🇫🇷", es: "🇪🇸", pt: "🇧🇷", it: "🇮🇹", nl: "🇳🇱", sv: "🇸🇪",
  no: "🇳🇴", da: "🇩🇰", fi: "🇫🇮", ro: "🇷🇴", bg: "🇧🇬", hr: "🇭🇷",
  uk: "🇺🇦", tr: "🇹🇷", ja: "🇯🇵", ko: "🇰🇷", zh: "🇨🇳", hi: "🇮🇳",
  ar: "🇸🇦", id: "🇮🇩"
};

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [activeLang, setActiveLang] = useState("");
  const [detectedLang, setDetectedLang] = useState("en");
  const [total, setTotal] = useState(0);

  useEffect(() => {
    // Detect user language
    const detectLang = async () => {
      try {
        const res = await axios.get(`${API}/detect-location`);
        const lang = res.data.suggested_language || "en";
        setDetectedLang(lang);
        setActiveLang(lang);
      } catch {
        const bl = navigator.language.split("-")[0];
        setDetectedLang(bl);
        setActiveLang(bl);
      }
    };
    detectLang();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = new URLSearchParams();
        if (activeCategory) params.append("category", activeCategory);
        if (activeLang) params.append("lang", activeLang);
        params.append("limit", "30");
        const [postsRes, catsRes] = await Promise.all([
          axios.get(`${API}/blog/posts?${params}`),
          axios.get(`${API}/blog/categories`)
        ]);
        setPosts(postsRes.data.posts);
        setTotal(postsRes.data.total);
        setCategories(catsRes.data.categories);
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, [activeCategory, activeLang]);

  // Get unique languages from posts
  const availableLangs = [...new Set(posts.map(p => p.language).filter(Boolean))];

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <header className="bg-[var(--color-bg)]/60 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-5 h-5 text-white" /></div>
            <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors font-['JetBrains_Mono'] uppercase">Home</a>
            <span className="text-[#a855f7] font-['JetBrains_Mono'] text-sm uppercase tracking-wider font-bold">Blog</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-2" data-testid="blog-title">TopRankPulse Blog</h1>
        <p className="text-[var(--color-text-muted)] mb-8">{total} articles published</p>

        {/* Language Filter */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Globe className="w-4 h-4 text-[var(--color-text-muted)]" />
          <button onClick={() => setActiveLang("")} className={`px-3 py-1.5 text-sm transition-colors ${!activeLang ? 'bg-[#a855f7] text-white' : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] border border-[var(--color-border)] hover:text-[var(--color-text)]'}`} data-testid="lang-filter-all">All</button>
          {Object.entries(LANG_FLAGS).slice(0, 12).map(([code, flag]) => (
            <button key={code} onClick={() => setActiveLang(code)} className={`px-3 py-1.5 text-sm transition-colors flex items-center gap-1 ${activeLang === code ? 'bg-[#a855f7] text-white' : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] border border-[var(--color-border)] hover:text-[var(--color-text)]'}`} data-testid={`lang-filter-${code}`}>
              <span>{flag}</span>
              <span className="hidden sm:inline font-['JetBrains_Mono'] text-xs uppercase">{code}</span>
            </button>
          ))}
          <button onClick={() => {
            const el = document.getElementById('more-langs');
            if (el) el.classList.toggle('hidden');
          }} className="px-3 py-1.5 text-sm bg-[var(--color-surface)] text-[var(--color-text-muted)] border border-[var(--color-border)] hover:text-[var(--color-text)]">+14</button>
        </div>
        <div id="more-langs" className="hidden flex flex-wrap gap-2 mb-4">
          {Object.entries(LANG_FLAGS).slice(12).map(([code, flag]) => (
            <button key={code} onClick={() => setActiveLang(code)} className={`px-3 py-1.5 text-sm transition-colors flex items-center gap-1 ${activeLang === code ? 'bg-[#a855f7] text-white' : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] border border-[var(--color-border)] hover:text-[var(--color-text)]'}`}>
              <span>{flag}</span>
              <span className="font-['JetBrains_Mono'] text-xs uppercase">{code}</span>
            </button>
          ))}
        </div>

        {/* Categories */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <button onClick={() => setActiveCategory("")} className={`px-4 py-2 text-sm font-semibold transition-colors ${!activeCategory ? 'bg-zinc-700 text-white' : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] border border-[var(--color-border)] hover:text-[var(--color-text)]'}`} data-testid="blog-cat-all">All Topics</button>
            {categories.map((cat, i) => (
              <button key={i} onClick={() => setActiveCategory(cat.name)} className={`px-4 py-2 text-sm font-semibold transition-colors ${activeCategory === cat.name ? 'bg-zinc-700 text-white' : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] border border-[var(--color-border)] hover:text-[var(--color-text)]'}`} data-testid={`blog-cat-${i}`}>
                {cat.name} ({cat.post_count})
              </button>
            ))}
          </div>
        )}

        {/* Posts */}
        {posts.length === 0 ? (
          <div className="text-center py-20">
            <Search className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-[var(--color-text-muted)] text-lg">No articles yet</p>
            <p className="text-[var(--color-text-muted)] text-sm mt-2">Articles are published daily by our AI Blog Agent</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post, i) => (
              <a key={i} href={`/blog/${post.slug}`} className="bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-[var(--color-primary)] transition-all group" data-testid={`blog-post-${i}`}>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#a855f7] font-['JetBrains_Mono'] uppercase tracking-wider">{post.category}</span>
                    <span className="text-lg" title={post.language_name}>{post.flag || LANG_FLAGS[post.language] || "🇬🇧"}</span>
                  </div>
                  <h2 className="text-lg font-bold group-hover:text-[#a855f7] transition-colors line-clamp-2 mb-3">{post.title}</h2>
                  <p className="text-[var(--color-text-muted)] text-sm line-clamp-2 mb-4">{post.meta_description}</p>
                  <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)]">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(post.published_at).toLocaleDateString()}</span>
                      <span>{post.word_count} words</span>
                    </div>
                    <span className="text-emerald-500 font-bold font-['JetBrains_Mono']">{post.seo_score}/100</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
